import _ from 'underscore';
import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { mergeClasses } from '@fluentui/react-components';
import {
  AdsStudioLibraryPageContent,
  AdsStudioFoldersGridCell,
  GridSectionName,
  ImagesFilterBar,
  FolderPathBreadcrumb,
  GridCellFactory,
  LibraryEmptyView,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

import { useBaseTargetFolderPickerStyles, useTargetFolderPickerStyle } from './target-folder-picker-style';

export const AdsStudioTargetFolderPicker = ({
  selectedFolder,
  setSelectedFolder,
  dataService,
  i18n,
  dateFormat,
  ianaTimeZone,
  assetLibraryUserPreferences,
  logger,
  notificationApi,
  permissions,
}) => {
  const classes = useTargetFolderPickerStyle();
  const baseClassName = useBaseTargetFolderPickerStyles();
  const resetSelectionState = useCallback(() => {
    setSelectedFolder(null);
  }, [setSelectedFolder]);

  const {
    folders, navigateIntoFolder, folderPath, navigateToFolderInPath,
  } = dataService;

  useEffect(() => {
    if (selectedFolder && !_.find(folders, f => f === selectedFolder)) {
      resetSelectionState();
    }
  }, [folders, resetSelectionState, selectedFolder]);

  const renderTilesGridCell = useCallback(({
    item, index, searchText, sectionName,
  }) => {
    const onFolderClick = () => {
      const { folderId } = item || {};
      if (folderId) {
        navigateIntoFolder({ id: folderId });
        resetSelectionState();
        logger.logTrace('Navigate into folder in target folder picker', { folderId });
      }
    };

    // folder cell
    if (sectionName === GridSectionName.Folder) {
      return (
        <div className={classes.targetFolderPickerFolderCell}>
          <AdsStudioFoldersGridCell
            item={item}
            highlightedText={searchText}
            onFolderClick={onFolderClick}
            i18n={i18n}
          />
        </div>
      );
    }
    // image cell
    return (
      <div className="target-folder-picker-asset-cell">
        <GridCellFactory
          item={item}
          index={index}
          i18n={i18n}
          highlightedText={searchText}
        />
      </div>
    );
  }, [navigateIntoFolder, resetSelectionState, logger, classes.targetFolderPickerFolderCell, i18n]);

  const onFailedToLoadData = useCallback(() => {
    notificationApi.showWarning(i18n.getString(_TL_('Failed to load assets. Please refresh the page and try again.')));
  }, [i18n, notificationApi]);

  const onFolderPathClicked = useCallback((item) => {
    navigateToFolderInPath({ id: item.key });
  }, [navigateToFolderInPath]);

  const onRenderHeader = useCallback(() => (
    <React.Fragment>
      <div className={classes.filterContainer}><ImagesFilterBar i18n={i18n} /></div>
      <div className={classes.folderPathContainer}>
        <FolderPathBreadcrumb
          folderPath={folderPath}
          onItemClick={onFolderPathClicked}
          visibleForRootFolder
        />
      </div>
    </React.Fragment>
  ), [classes, folderPath, i18n, onFolderPathClicked]);

  const onRenderEmptyView = useCallback(() => (
    <LibraryEmptyView
      i18n={i18n}
      hideAddAssetCards
    />
  ), [i18n]);

  return (
    <div className={mergeClasses(baseClassName, classes.targetFolderPickerViewContainer)} data-is-scrollable>
      <AdsStudioLibraryPageContent
        dataService={dataService}
        renderGridCell={renderTilesGridCell}
        dateFormat={dateFormat}
        ianaTimeZone={ianaTimeZone}
        assetLibraryUserPreferences={assetLibraryUserPreferences}
        logger={logger}
        i18n={i18n}
        onFailedToLoadData={onFailedToLoadData}
        displayFolderPath
        onFolderNavigationChange={resetSelectionState}
        renderHeader={onRenderHeader}
        gridScrollContainer=".target-folder-picker-wrapper"
        renderEmptyView={onRenderEmptyView}
        permissions={permissions}
      />
    </div>
  );
};

AdsStudioTargetFolderPicker.propTypes = {
  selectedFolder: PropTypes.shape({}),
  setSelectedFolder: PropTypes.func.isRequired,
  dataService: PropTypes.shape({
    folders: PropTypes.arrayOf(PropTypes.shape({})),
    navigateIntoFolder: PropTypes.func,
    folderPath: PropTypes.arrayOf(PropTypes.shape({})),
    navigateToFolderInPath: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
    logError: PropTypes.func,
  }).isRequired,
  ianaTimeZone: PropTypes.string.isRequired,
  dateFormat: PropTypes.string.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  assetLibraryUserPreferences: PropTypes.shape({}),
  notificationApi: PropTypes.shape({
    showWarning: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({}),
  }),
};

AdsStudioTargetFolderPicker.defaultProps = {
  selectedFolder: null,
  assetLibraryUserPreferences: null,
  permissions: { dynamic: {} },
};

